import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box, TextField, useTheme } from "@mui/material";

import { QueryField, QueryFieldOption, QueryInfo, sortFieldsByTypeAndLabel } from "system/query/QueryInfo";
import { ComparisonDraftColumn } from "./QueryComparisonTableSettings";
import { aggOperatorsByType, OpOption } from "system/query/Aggs";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { ColumnDialogMode, ColumnRenderType } from "components/tables/columns/ColumnModel";

interface ComparisonTableDialogContentProps {
  activeColumnIndex: number | undefined;
  aggFieldsInUse?: string[];
  columnDialogMode: ColumnDialogMode | undefined;
  draftColumn: ComparisonDraftColumn | undefined;
  onSetDraftColumn: (draftColumn: ComparisonDraftColumn) => void;
  queryInfo: QueryInfo;
  usuableFields?: QueryField[];
}

export default function ComparisonTableDialogContent(props: ComparisonTableDialogContentProps) {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const renderTypes = [
    { value: ColumnRenderType.Number, label: i18n.t("number") },
    { value: ColumnRenderType.Percent, label: i18n.t("percent") }
  ];
  
  const [selectedField, setSelectedField] = useState<QueryField | null>(
    props.draftColumn?.field ??
    props.usuableFields?.find(f => f.id === "__count__") ??
    props.usuableFields?.[0] ??
    null
  );
  const [selectedFieldOption, setSelectedFieldOption] = useState<QueryFieldOption | null>(props.draftColumn?.option ?? selectedField?.options?.[0] ?? null);

  const [aggOperators, setAggOperators] = useState<OpOption[] | null>();
  const [selectedAggOp, setSelectedAggOp] = useState<OpOption | null>();
  const [renderType, setRenderType] = useState<{ value: ColumnRenderType, label: string } | undefined>(() => {
    return props.draftColumn?.renderType ?
      renderTypes.find((t) => t.value === props.draftColumn?.renderType) :
      renderTypes.find((t) => t.value === ColumnRenderType.Number);
  });

  const sortedQueryFields = sortFieldsByTypeAndLabel(props.usuableFields ?? []);

  function handleFieldChange(field: QueryField) {
    if (!field) {
      setAggOperators([]);
      return;
    }

    // Records doesn't need an operator & Social Universe is its own operator
    const intOperatorOptions = !["__count__", "social_universe"].includes(field.id) ?
      aggOperatorsByType(field, i18n.t, props.queryInfo?.supported_aggregation_ops) : [];
    // Set a default operator option so user can click "save" when dialog first options
    const intOpOption = intOperatorOptions?.find((o: { id: string, label: string }) => o.id === "avg") || intOperatorOptions?.[0] || null;
    setAggOperators(intOperatorOptions);

    let newDraftColumn: ComparisonDraftColumn = {
      field: field,
      renderType: ColumnRenderType.Number,
      ...((intOperatorOptions && intOpOption) && {
        operator: intOpOption
      }),
      ...(field.options && {
        option: field.options[0],
      }),
    };

    if (props.columnDialogMode === ColumnDialogMode.UPDATE_COLUMN) {
      newDraftColumn = {
        ...newDraftColumn,
        ...((props.activeColumnIndex !== null && props.activeColumnIndex !== undefined) && {
          activeColumnIndex: props.activeColumnIndex
        }),
      }
    }

    intOperatorOptions && setSelectedAggOp(intOpOption);
    field.options && setSelectedFieldOption(field.options[0]);
    props.onSetDraftColumn(newDraftColumn);
  }

  return (
    <>
      <Autocomplete
        value={selectedField}
        options={sortedQueryFields}
        groupBy={(option) => option._social_field_category || option.type}
        onChange={(e, newValue: QueryField | null) => {
          if (newValue) {
            setSelectedField(newValue);
            handleFieldChange(newValue);
          }
        }}
        renderInput={params => <TextField {...params} />}
        renderGroup={(params) => (
          <Box key={params.key}>
            <Box sx={{ fontWeight: "bold", p: 1, backgroundColor: theme.palette.background.default }}>
              {i18n.t([`query-social-field-categories.${params.group}`, params.group])}
            </Box>
            <ul>{params.children}</ul>
          </Box>
        )}
      />
      {!!aggOperators?.length && (
        <>
          <SimpleFormLabel>{i18n.t("filterable-table.operator")}</SimpleFormLabel>
          <Autocomplete
            value={selectedAggOp}
            options={aggOperators}
            renderInput={params => <TextField {...params} />}
            onChange={(e, newValue) => {
              if (newValue && props.draftColumn) {
                setSelectedAggOp(newValue);
                props.onSetDraftColumn({
                  ...props.draftColumn,
                  operator: newValue
                });
              }
            }}
          />
        </>
      )}
      {selectedField?.options?.length && (
        <>
          <SimpleFormLabel>{i18n.t("field-option")}</SimpleFormLabel>
          <Autocomplete
            value={selectedFieldOption}
            options={selectedField?.options}
            onChange={(e, newValue: QueryFieldOption | null) => {
              if (newValue && props.draftColumn) {
                setSelectedFieldOption(newValue);
                props.onSetDraftColumn({
                  ...props.draftColumn,
                  option: newValue,
                });
              }
            }}
            renderInput={params => <TextField {...params} />}
          />
        </>
      )}
      {selectedField?.options?.length && (
        <>
          <SimpleFormLabel>{i18n.t("tables.display-type")}</SimpleFormLabel>
          <Autocomplete
            value={renderType}
            defaultValue={renderTypes[0]}
            options={renderTypes}
            renderInput={params => <TextField {...params} />}
            onChange={(e, newValue: any) => {
              if (newValue && props.draftColumn) {
                setRenderType(newValue);
                props.onSetDraftColumn({
                  ...props.draftColumn,
                  renderType: newValue.value
                });
              }
            }}
          />
        </>
      )}
    </>
  );
}
